import type { ReactNode } from 'react';

import {
  BLOCKED_SCOPE_LABEL,
  STATUS_PRESENTATION,
  TASK_STATE_DOT,
  taskStateWords,
  type WorkspaceTaskRow,
} from '@/features/tasks';
import { useCanWork } from '@/features/workspaces/canWork';
import {
  ordinal,
  queuedReasonSentence,
  useWorkspaceConcurrencyLimit,
} from '@/features/concurrency';
import { StepKindIcon } from '@/shared/components/StepKindIcon';
import type { TaskListDensity } from '@/shared/store/preferences';

/*
  The two cells of the Tasks table with more to say than one value (spec 110).
  Their own file because the column definitions beside them export data, and a
  module that mixes components with other exports cannot be fast-refreshed.
*/

export function TaskCell({
  row,
  density = 'cozy',
}: {
  row: WorkspaceTaskRow;
  density?: TaskListDensity;
}) {
  const compact = density === 'compact';
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-2">
        <span
          className={`truncate text-sm font-medium ${row.hidden ? 'text-muted-foreground' : 'text-foreground'}`}
          // One line in Compact (spec 114 US2): the second line's workflow and
          // step are still one hover away, rather than gone.
          title={
            compact
              ? `${row.title} — ${row.workflowName} · ${row.stepLabel ?? 'No step'}`
              : row.title
          }
        >
          {row.title}
        </span>
        {row.hidden ? <HiddenMarker /> : null}
      </span>
      {compact ? null : (
        <span className="text-muted-foreground truncate text-xs">
          <TaskMetaLine row={row} />
        </span>
      )}
    </div>
  );
}

/**
 * What the task's second line says: its workflow, its step and who holds it,
 * and the occasional counts. The table's Task cell and the phone row both
 * draw it, so they cannot say different things.
 */
export function TaskMetaLine({ row }: { row: WorkspaceTaskRow }) {
  return (
    <>
      {row.workflowName}
      {' · '}
      {row.orphaned ? (
        // Marked, not blanked: being out of place is the most useful thing
        // this row can say about a task standing nowhere.
        <span className="text-board-state-failed">
          Out of place — its step no longer exists
        </span>
      ) : (
        <>
          {/* Whether an agent or a person holds the step — the difference
            between a task that will move on its own and one that will not. */}
          <StepKindIcon kind={row.stepKind} /> {row.stepLabel ?? 'No step'}
        </>
      )}
      {!row.hasBoard ? ' · Draft workflow — no board yet' : null}
      {/*
      Occasional signals ride on this line rather than in columns of their
      own: most rows have neither, a column would stand empty, and the line
      truncates, so a count is the right thing to lose first when the width
      runs out. Same words as the board card, which reads the same fields.
    */}
      {row.linkedTasks
        ? ` · ${String(row.linkedTasks.done)}/${String(row.linkedTasks.total)} created tasks`
        : null}
      {row.commentCount > 0
        ? ` · ${String(row.commentCount)} ${row.commentCount === 1 ? 'comment' : 'comments'}`
        : null}
    </>
  );
}

/**
 * The run state as a dot and a word, as before — except where the list knows
 * more than the four buckets say.
 *
 * A blocked task reads as the board's "Spend limit", with the board's glyph and
 * colour, instead of "At rest"; a task on the done column reads "Completed".
 * Neither changes which bucket the task is *filtered* in (research R2): these
 * are words for the reader, not new states. Review and attention flags sit on a
 * second line, as they sit in their own row on the card.
 *
 * **What needs a person is tinted** (spec 113 FR-006). Errors, Waiting on you
 * (a reader's Waiting on a member, spec 193) and Spend limit — and the Review
 * and Attention flags — sit on their state's colour at 12%, the board's own
 * tint; In progress, At rest and Completed keep the plain mark and word. The
 * tiles' rule, carried onto the rows: colour means someone should look.
 * Finding three broken tasks among a hundred used to mean finding three 8px
 * dots.
 */
export function StateCell({
  row,
  density = 'cozy',
}: {
  row: WorkspaceTaskRow;
  density?: TaskListDensity;
}) {
  return (
    <div
      className={`flex min-w-0 text-xs ${
        // Compact has one line, so the flags sit beside the state (spec 114).
        density === 'compact'
          ? 'flex-row items-center gap-1.5'
          : 'flex-col items-start gap-0.5'
      }`}
    >
      <StateWord row={row} />
      {row.needsReview || row.needsAttention ? (
        <span className="flex min-w-0 gap-1">
          {row.needsAttention ? (
            <span data-flag-chip className={`${FLAG_CHIP} ${TINT.failed}`}>
              Attention
            </span>
          ) : null}
          {row.needsReview ? (
            <span data-flag-chip className={`${FLAG_CHIP} ${TINT.waiting}`}>
              Review
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/** The board's 12% tint and its text, per role (spec 113 research R4). */
const TINT = {
  failed: 'bg-board-state-failed/12 text-board-state-failed',
  waiting: 'bg-board-state-waiting/12 text-board-state-waiting',
} as const;

/*
  Pulled left by exactly its own padding, so the mark inside a chip starts at
  the same x as the mark of a plain row — the column still reads as one (see
  `MARK_SLOT`).
*/
const STATE_CHIP =
  '-ml-1.5 flex max-w-full items-center gap-1.5 rounded-full px-1.5 py-0.5 font-medium';

const FLAG_CHIP = 'rounded-sm px-1.5 text-xs leading-4 font-medium';

/**
 * One width for every state mark — dot or icon — so the words line up.
 *
 * Lifted one pixel. Flex centring puts the mark on the middle of the line box,
 * and the line box includes the room reserved for descenders, so the mark sat
 * below the letters' optical centre: measured on a real render, the 14px icon
 * ran from the capitals' top to 4px *below* the baseline (centre 27 against a
 * cap-band centre of 25.5 and an x-height centre of 27). One pixel up puts it
 * between the two, where the eye reads "level".
 */
const MARK_SLOT =
  'flex size-3.5 shrink-0 -translate-y-px items-center justify-center';

function StateWord({ row }: { row: WorkspaceTaskRow }) {
  const workspaceLimit = useWorkspaceConcurrencyLimit();
  // A reader is told a gate waits on a member, not on them (spec 193).
  const words = taskStateWords(useCanWork());
  // Blocked for want of its person's own account (spec 160): a key and its own
  // words, never the spend limit's.
  const needsAccount = STATUS_PRESENTATION['needs-account'];
  if (row.blocked === 'account') {
    if (!needsAccount) return null;
    const { Icon } = needsAccount;
    return (
      <span
        data-state-chip
        className={`${STATE_CHIP} ${TINT.waiting}`}
        title={needsAccount.canonicalName}
      >
        <span aria-hidden="true" className={MARK_SLOT}>
          <Icon className="size-3.5" />
        </span>
        {needsAccount.cardLabel}
      </span>
    );
  }
  const blocked = STATUS_PRESENTATION.blocked;
  if (row.blocked !== null && blocked) {
    const { Icon } = blocked;
    return (
      <span
        data-state-chip
        className={`${STATE_CHIP} ${TINT.waiting}`}
        title={
          row.blocked === 'unknown'
            ? blocked.canonicalName
            : BLOCKED_SCOPE_LABEL[row.blocked]
        }
      >
        <span aria-hidden="true" className={MARK_SLOT}>
          <Icon className="size-3.5" />
        </span>
        {blocked.cardLabel}
      </span>
    );
  }

  // Waiting its turn (spec 115 FR-004): the clock and "Queued", its place beside
  // them, and the reason on hover and to assistive technology.
  const queued = STATUS_PRESENTATION.queued;
  if (row.queued !== null && queued) {
    const { Icon } = queued;
    const reason = queuedReasonSentence(row.queued.reason, workspaceLimit);
    return (
      <span
        className={`flex items-center gap-1.5 ${queued.colourClass}`}
        title={reason ?? undefined}
      >
        <span aria-hidden="true" className={MARK_SLOT}>
          <Icon className="size-3.5" />
        </span>
        {queued.canonicalName}
        {row.queued.position !== null
          ? ` · ${ordinal(row.queued.position)}`
          : null}
        {reason ? <span className="sr-only">{reason}</span> : null}
      </span>
    );
  }

  const completed = STATUS_PRESENTATION.completed;
  if (row.finished && row.state === 'at-rest' && completed) {
    const { Icon } = completed;
    return (
      <span className={`flex items-center gap-1.5 ${completed.colourClass}`}>
        <span aria-hidden="true" className={MARK_SLOT}>
          <Icon className="size-3.5" />
        </span>
        {completed.canonicalName}
      </span>
    );
  }

  // A dot *and* a word: the dot carries the board's role colour so the two
  // surfaces agree at a glance; the word is there because colour is never
  // allowed to be the only thing saying what state something is in.
  //
  // The dot sits centred in a slot as wide as the icons above (`MARK_SLOT`),
  // so every state word in the column starts at the same x. An 8px dot beside
  // a 14px icon made "Completed" and "Spend limit" start 6px right of every
  // other word, and the column stopped reading as one.
  const tint =
    row.state === 'failed'
      ? TINT.failed
      : row.state === 'waiting'
        ? TINT.waiting
        : null;
  return (
    <span
      data-state-chip={tint ? '' : undefined}
      className={
        tint
          ? `${STATE_CHIP} ${tint}`
          : 'text-muted-foreground flex items-center gap-1.5'
      }
    >
      <span aria-hidden="true" className={MARK_SLOT}>
        <span className={`size-2 rounded-full ${TASK_STATE_DOT[row.state]}`} />
      </span>
      {words[row.state].one}
    </span>
  );
}

/**
 * A hidden task, listed because the reader asked to see hidden tasks (spec 112
 * US5), said in words. Bringing it back is the row's Unhide action, beside the
 * row link rather than inside it (spec 114 FR-014).
 */
function HiddenMarker() {
  return (
    <span className="text-muted-foreground border-border shrink-0 rounded-sm border border-dashed px-1 text-2xs">
      Hidden
    </span>
  );
}

/**
 * One task as a phone draws it (spec 265): the title gets the screen's width.
 *
 * Below 768px the table's three tracks left the title 70px at 360px wide, so
 * every task read "Hello wo…". Here the title and its time share the first
 * line and the state leads the second, before the workflow and step. Compact
 * keeps one line: title, state, time. Same words as the table's cells, and the
 * same fixed height, so the virtualiser still measures by `taskRowHeight`.
 */
export function PhoneTaskSummary({
  row,
  density = 'cozy',
  updated,
}: {
  row: WorkspaceTaskRow;
  density?: TaskListDensity;
  /** The Updated cell, as the table renders it. */
  updated: ReactNode;
}) {
  const compact = density === 'compact';
  const time = (
    <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
      <span className="sr-only">Updated </span>
      {updated}
    </span>
  );
  const title = (
    <span
      className={`min-w-0 flex-1 truncate text-sm font-medium ${row.hidden ? 'text-muted-foreground' : 'text-foreground'}`}
      title={row.title}
    >
      {row.title}
    </span>
  );
  if (compact) {
    return (
      <div className="flex min-w-0 items-center gap-3 text-xs">
        {title}
        {row.hidden ? <HiddenMarker /> : null}
        <span className="flex shrink-0 items-center whitespace-nowrap">
          <StateWord row={row} />
        </span>
        {time}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-3">
        {title}
        {row.hidden ? <HiddenMarker /> : null}
        {time}
      </div>
      <div className="flex min-w-0 items-center gap-2 text-xs">
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap">
          <StateWord row={row} />
          {row.needsAttention ? (
            <span data-flag-chip className={`${FLAG_CHIP} ${TINT.failed}`}>
              Attention
            </span>
          ) : null}
          {row.needsReview ? (
            <span data-flag-chip className={`${FLAG_CHIP} ${TINT.waiting}`}>
              Review
            </span>
          ) : null}
        </span>
        <span className="text-muted-foreground min-w-0 truncate">
          <TaskMetaLine row={row} />
        </span>
      </div>
    </div>
  );
}
